import { useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isStandalone, setIsStandalone] = useState(false)
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    const checkStandalone = () => {
      const isStandaloneMode =
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as any).standalone === true
      setIsStandalone(isStandaloneMode)
    }

    const checkIOS = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera
      const hasTouch = 'ontouchend' in document
      // Check for iPhones/iPads/iPods, including newer iPads that identify as Macintosh desktops
      const isApple = /iPad|iPhone|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && hasTouch)
      setIsIOS(!!isApple)
    }

    checkStandalone()
    checkIOS()

    const handleBeforeInstall = (e: Event) => {
      // Prevent default browser install bar from showing up automatically
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
    }
  }, [])

  const triggerInstall = async () => {
    if (!deferredPrompt) return false
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setDeferredPrompt(null)
      return true
    }
    return false
  }

  return {
    isInstallable: deferredPrompt !== null,
    isIOS,
    isStandalone,
    triggerInstall,
  }
}
